import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { canonicalCitySlug, cityNameFromSlug } from '../constants/cities';
import EventCard from '../components/events/EventCard';
import { useTheme } from '../context/ThemeContext';
import { getEvents } from '../utils/api';
import useWindowWidth from '../hooks/useWindowWidth';


export default function CityEventsPage() {
  const { VB } = useTheme();
  const { citySlug, id } = useParams();
  const resolvedCitySlug = citySlug || id || '';
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const cityName = useMemo(() => cityNameFromSlug(resolvedCitySlug), [resolvedCitySlug]);
  // /events/dallas and /events/dallas-fort-worth are the same page for the same
  // city. Pointing both at one address keeps Dallas from competing with itself
  // in search results; self-canonicalising made them two rival pages.
  const canonicalSlug = useMemo(() => canonicalCitySlug(resolvedCitySlug), [resolvedCitySlug]);
  const canonicalUrl = `https://vibana.app/events/${canonicalSlug}`;

  useEffect(() => {
    let mounted = true;
    const loadCityEvents = async () => {
      setLoading(true);
      try {
        const response = await getEvents({ city: cityName });
        const fetchedEvents = response?.data?.events || [];
        if (mounted) setEvents(fetchedEvents);
      } catch {
        if (mounted) setEvents([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadCityEvents();
    return () => { mounted = false; };
  }, [cityName]);

  const gridTemplateColumns = isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(300px, 1fr))';
  const gridGap = isMobile ? 16 : 24;
  const pagePadding = isMobile ? '24px 16px 64px' : isTablet ? '28px 24px 64px' : '32px 40px 64px';

  return (
    <div style={{ background: VB.void, minHeight: '100vh' }}>
      <style>{`
        .vb-events-card { transition: transform 0.15s, box-shadow 0.15s; }
        .vb-events-card:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,0,0,0.4); }
      `}</style>
      <Helmet>
        <title>African Events in {cityName} — Vibana</title>
        <meta name="description" content={`Explore African events happening in ${cityName}. Discover concerts, nightlife, culture, and community on Vibana.`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`African Events in ${cityName} — Vibana`} />
        <meta property="og:description" content={`Discover concerts, nightlife, food, culture and more in ${cityName} on Vibana.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://vibana.app/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`African Events in ${cityName} — Vibana`} />
        <meta name="twitter:description" content={`Discover concerts, nightlife, food, culture and more in ${cityName} on Vibana.`} />
        <meta name="twitter:image" content="https://vibana.app/og-image.jpg" />
      </Helmet>

      <div style={{ padding: pagePadding }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontSize: 13, color: VB.snowMuted }}>
          <Link to="/" style={{ color: VB.snowMuted, textDecoration: 'none', fontWeight: 700 }}>
            vibana
          </Link>
          <span style={{ opacity: 0.5 }}>›</span>
          <Link to="/events" style={{ color: VB.snowMuted, textDecoration: 'none' }}>Events</Link>
          <span style={{ opacity: 0.5 }}>›</span>
          <span style={{ color: VB.snowPrimary, fontWeight: 600 }}>{cityName}</span>
        </div>

        <h1 style={{ margin: 0, fontSize: 'clamp(30px,4.4vw,44px)', fontWeight: 500, color: VB.snowPrimary, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
          African events in {cityName}
        </h1>
        <p style={{ marginTop: 10, marginBottom: 6, color: VB.snowMuted, fontSize: 15, maxWidth: 560, lineHeight: 1.6 }}>
          Discover concerts, nightlife, food, culture, and more in {cityName} — curated by the Vibana community.
        </p>
        <Link to="/events" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 28, fontSize: 13, color: VB.snowMuted, textDecoration: 'none', fontWeight: 600 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
          Browse all events
        </Link>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns, gap: gridGap }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ borderRadius: 16, overflow: 'hidden', background: VB.darkCard, border: `0.5px solid ${VB.borderDark}` }}>
                <div style={{ height: 200 }} className="vb-skeleton" />
                <div style={{ padding: 18 }}>
                  <div className="vb-skeleton" style={{ height: 13, width: '40%', borderRadius: 4, marginBottom: 10 }} />
                  <div className="vb-skeleton" style={{ height: 18, width: '80%', borderRadius: 4, marginBottom: 10 }} />
                  <div className="vb-skeleton" style={{ height: 13, width: '60%', borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, background: VB.darkCard, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: `1px solid ${VB.borderDark}` }}>
              <svg width="28" height="28" fill="none" stroke={VB.snowMuted} strokeWidth="2.2" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <h3 style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', marginBottom: 10, color: VB.snowPrimary, letterSpacing: '-0.4px' }}>
              No events in {cityName} right now
            </h3>
            <p style={{ color: VB.snowMuted, fontSize: 15, maxWidth: 320, margin: '0 auto 28px', lineHeight: 1.6 }}>
              Check back soon, or browse all upcoming African events across North America.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/events" style={{ padding: '11px 20px', borderRadius: 8, background: VB.darkCard, border: `1px solid ${VB.borderDark}`, color: VB.snowPrimary, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                Browse all events
              </Link>
              <Link to="/submit-event" style={{ padding: '11px 20px', borderRadius: 8, background: '#E63946', color: '#FFFFFF', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                Submit an event
              </Link>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns, gap: gridGap }}>
            {events.map(event => (
              <div key={event._id} className="vb-events-card">
                <EventCard event={event} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
